import React, { useEffect, useState } from 'react'
import { AlertTriangleIcon } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

import { Button, Input, Modal } from '@repo/ui'
import { supportApi } from 'services'
import { MESSAGE_FIELD_SX } from 'pages/support/messageField'
import type { AutoErrorReportedFlag } from 'utils/errorReporter'
import { AUTO_ERROR_REPORTED_FLAG_KEY } from 'utils/errorReporter'

/** Matches the API's own cap (`MAX_ERROR_REPORT_NOTE`), so nothing is truncated server-side. */
const NOTE_MAX_LENGTH = 2000

/**
 * VRG-427 — tells the user their crash was reported, on the page they were sent
 * BACK to. Deliberately never rendered on the dead screen itself (a modal over a
 * broken page is just more broken page — see the task's product decisions).
 *
 * Driven by a `sessionStorage` flag rather than router/props: the recovery
 * redirect in `utils/errorReporter.ts` is a HARD navigation (a fresh JS
 * context, by design — see that file for why), so nothing in React state can
 * carry across it. `sessionStorage` is the one thing that survives a same-tab
 * reload without surviving a genuinely new session, which is exactly the
 * lifetime this flag needs: read once, on the very next page, then cleared.
 *
 * Mount ONCE, near the app root — same place as `useTrackRouteForRecovery`.
 *
 * ## The optional field, and why it is optional
 *
 * A crash is the worst possible moment to demand typing. The user has just lost
 * whatever they were doing and been moved to a different page; a required field
 * here would make a bad moment worse, and the thing it would extract under duress
 * is the least reliable version of the answer. So the field is genuinely optional:
 * "Got it" is always enabled, always closes, and never nags.
 *
 * What the field DOES decide is ownership. The report is filed either way — Vera
 * keeps the stack trace regardless — but it appears in the user's own support
 * requests only if they wrote something. That is the whole point of asking: a
 * ticket someone chose to comment on is one they may want to follow; an auto-filed
 * one they never opened is clutter in a list they are supposed to read.
 */
export const ErrorReportedModal = () => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  /**
   * The report the boundary already filed, if the network came back before the
   * recovery redirect. Undefined means it did not — the modal still tells the
   * truth ("this has been reported"), it just has nothing to attach words to, so
   * the field is not offered. Better than showing a field whose submit would have
   * nowhere to go.
   */
  const [reportId, setReportId] = useState<string | undefined>()
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(AUTO_ERROR_REPORTED_FLAG_KEY)
      if (!raw) return
      sessionStorage.removeItem(AUTO_ERROR_REPORTED_FLAG_KEY)

      /*
        The flag was a bare `'1'` before this change and is JSON now. Both are
        accepted, and the parse is guarded: a tab that navigated across a deploy
        can read a value the other version wrote, and a modal that throws while
        telling you about a crash is its own small joke.
      */
      let parsed: AutoErrorReportedFlag = {}
      try {
        const value = JSON.parse(raw)
        if (value && typeof value === 'object') parsed = value as AutoErrorReportedFlag
      } catch {
        /* legacy `'1'`, or anything else — treat as "reported, no id" */
      }

      setReportId(typeof parsed.reportId === 'string' ? parsed.reportId : undefined)
      setOpen(true)
    } catch {
      /* sessionStorage unavailable (private mode, etc) — nothing to show */
    }
  }, [])

  const trimmed = note.trim()

  const close = () => setOpen(false)

  const submit = async () => {
    if (!reportId || !trimmed || sending) return
    setSending(true)
    setFailed(false)
    try {
      await supportApi.addErrorReportNote(reportId, trimmed)
      setSent(true)
    } catch {
      // Inline, not a toast, and the modal stays open with the text intact. The
      // user typed this once under bad circumstances; losing it to a failed
      // request and asking them to type it again is not an option.
      setFailed(true)
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal
      title="Something went wrong"
      titleIcon={<AlertTriangleIcon size={20} strokeWidth={1.5} color={theme.palette.error.main} />}
      open={open}
      onClose={close}
      actions={
        <Stack direction="row" spacing={3} alignItems="center">
          {/* Only rendered when there is something to send. Present-but-disabled
              would be wrong here: this is not a permission or an in-flight block
              (§46), it is a field the user has chosen not to fill, and a greyed
              "Send" beside an empty optional box reads as a demand. */}
          {reportId && !sent && (
            <Button
              variant="secondary"
              onClick={submit}
              disabled={!trimmed || sending}
              sx={{ textTransform: 'none' }}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          )}
          <Button variant="primary" onClick={close} sx={{ textTransform: 'none' }}>
            {sent ? 'Done' : 'Got it'}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
          There was an error, this has been reported to Vera. We&apos;ll notify you when this is
          fixed.
        </Typography>

        {reportId && !sent && (
          <Input
            label="What were you doing?"
            labelAdornment="(optional)"
            placeholder="Anything you remember helps. Skip this if you'd rather not."
            value={note}
            onValueChange={value => {
              setNote(value)
              if (failed) setFailed(false)
            }}
            multiline
            minRows={3}
            // `inputProps`, not a top-level prop — `Input` forwards this straight to
            // the underlying textarea, and there is no `maxLength` on `InputProps`.
            inputProps={{ maxLength: NOTE_MAX_LENGTH }}
            // 14px, matching the reply and intake composers: what is typed here
            // becomes a message on the report's thread, rendered back at body3.
            sx={MESSAGE_FIELD_SX}
          />
        )}

        {reportId && !sent && (
          <Typography variant="body4" sx={{ lineHeight: 1.5, color: 'text.secondary' }}>
            Add a note and this appears in your support requests, so you can follow it. Leave it
            blank and we&apos;ll still look into it.
          </Typography>
        )}

        {failed && (
          <Typography variant="body4" sx={{ lineHeight: 1.5, color: 'error.main' }}>
            That didn&apos;t send. Your note is still here, try again.
          </Typography>
        )}

        {sent && (
          <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
            Thanks. You&apos;ll find this in your support requests.
          </Typography>
        )}
      </Stack>
    </Modal>
  )
}
